import { useState, useEffect, useRef } from "react";
import actions from "../config.json";

/**
 * Hook to fetch and manage suggested places data based on search query
 * @param {string} queryValue - The search query string
 * @returns {Object} - Suggested places data and loading state
 */
export const useSuggestedPlaces = (queryValue) => {
  const [suggestedPlaces, setSuggestedPlaces] = useState([]);
  const [placeDetails, setPlaceDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [detailsError, setDetailsError] = useState(null);
  
  // Use refs to track request state without triggering re-renders
  const isMounted = useRef(true);
  const requestInProgress = useRef(false);
  const detailsRequestInProgress = useRef(false);
  const lastQuery = useRef(null);
  const lastPlaceId = useRef(null);

  /**
   * Fetch suggested places from the server
   * @param {string} query - The search query string
   */
  const fetchSuggestedPlaces = async (query) => {
    // Don't fetch if already fetching or query hasn't changed
    if (requestInProgress.current || query === lastQuery.current) {
      return;
    }
    
    // Validate input parameters
    if (!query || typeof query !== 'string' || query.trim() === '') {
      setError("Search query is required and should be a non-empty string");
      return;
    }

    // Track this request
    requestInProgress.current = true;
    lastQuery.current = query;
    
    setLoading(true);
    setError(null);

    const actionUrl = actions["marriott-passions-v3/get-suggested-places"];

    try {
      const response = await fetch(`${actionUrl}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          queryValue: query,
        }),
      });

      // Check if component is still mounted before updating state
      if (!isMounted.current) return;

      if (!response.ok) {
        throw new Error(`Error fetching suggested places: ${response.status}`);
      }

      const data = await response.json();
      
      // Check if component is still mounted before updating state
      if (!isMounted.current) return;

      // Extract the suggested places from the response
      const places = data.data?.suggestedPlaces?.edges || [];
      
      // Check if we actually got data back
      if (!data.data?.suggestedPlaces) {
        console.warn("No suggested places data returned from API");
      }
      
      setSuggestedPlaces(places);
    } catch (err) {
      // Check if component is still mounted before updating state
      if (!isMounted.current) return;
      
      console.error("Error fetching suggested places:", err);
      setError(err.message || "Failed to fetch suggested places");
    } finally {
      // Check if component is still mounted before updating state
      if (isMounted.current) {
        setLoading(false);
      }
      requestInProgress.current = false;
    }
  };

  /**
   * Refresh suggested places with a new query
   * @param {string} query - The search query string
   */
  const refreshSuggestedPlaces = (query) => {
    fetchSuggestedPlaces(query || queryValue);
  };
  
  /**
   * Fetch place details by place ID
   * @param {string} placeId - The place ID to fetch details for
   */
  const fetchPlaceDetails = async (placeId) => {
    // Don't fetch if already fetching or placeId hasn't changed
    if (detailsRequestInProgress.current || placeId === lastPlaceId.current) {
      return;
    }
    
    // Validate input parameters
    if (!placeId || typeof placeId !== 'string' || placeId.trim() === '') {
      setDetailsError("Place ID is required and should be a non-empty string");
      return;
    }

    // Track this request
    detailsRequestInProgress.current = true;
    lastPlaceId.current = placeId;
    
    setDetailsLoading(true);
    setDetailsError(null);

    const actionUrl = actions["marriott-passions-v3/get-suggested-place-details"];

    try {
      const response = await fetch(`${actionUrl}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          placeId: placeId,
        }),
      });

      // Check if component is still mounted before updating state
      if (!isMounted.current) return;

      if (!response.ok) {
        throw new Error(`Error fetching place details: ${response.status}`);
      }

      const data = await response.json();
      
      // Check if component is still mounted before updating state
      if (!isMounted.current) return;

      // Extract the place details from the response
      const details = data.data?.suggestedPlaceDetails || null;
      
      // Check if we actually got data back
      if (!details) {
        console.warn("No place details returned from API");
      }
      setPlaceDetails(details);

      return details;
    } catch (err) {
      // Check if component is still mounted before updating state
      if (!isMounted.current) return;
      
      console.error("Error fetching place details:", err);
      setDetailsError(err.message || "Failed to fetch place details");
    } finally {
      // Check if component is still mounted before updating state
      if (isMounted.current) {
        setDetailsLoading(false);
      }
      detailsRequestInProgress.current = false;
    }
  };

  /**
   * Format place name with primary and secondary descriptions
   * @param {Object} place - The place object from the API
   * @returns {string} - Formatted place name
   */
  const formatPlaceName = (place) => {
    if (!place || !place.node) return "";
    
    const node = place.node;
    const description = node.description || "";
    const primaryDesc = node.primaryDescription || "";
    const secondaryDesc = node.secondaryDescription || "";
    
    // If we have a full description, use that, otherwise construct from parts
    if (description) {
      return description;
    } else {
      return [primaryDesc, secondaryDesc].filter(Boolean).join(", ");
    }
  };
  
  /**
   * Get place ID from a place object
   * @param {Object} place - The place object from the API
   * @returns {string|null} - The place ID or null if not found
   */
  const getPlaceId = (place) => {
    if (!place || !place.node) return null;
    return place.node.placeId || null;
  };

  // Clean up on unmount
  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);
  
  // Fetch suggested places when queryValue changes
  useEffect(() => {
    if (queryValue && typeof queryValue === 'string' && queryValue.trim() !== '') {
      fetchSuggestedPlaces(queryValue);
    }
  }, [queryValue]); // Only depend on queryValue

  return {
    suggestedPlaces,
    placeDetails,
    loading,
    detailsLoading,
    error,
    detailsError,
    fetchSuggestedPlaces,
    refreshSuggestedPlaces,
    fetchPlaceDetails,
    formatPlaceName,
    getPlaceId,
  };
};